import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalCard } from '@/components/ui';
import { useForecastProject } from '@/hooks/useForecastProject';
import { ForecastProject } from '@/types/forecast';
import { error as logError } from '@/utils/logger';
import ForecastCard from './ForecastCard';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface AllForecastsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onForecastClick: (forecast: { id: string; name: string }) => void;
}

type ViewMode = 'grid' | 'list';

const AllForecastsModal: React.FC<AllForecastsModalProps> = ({
  isOpen,
  onClose,
  onForecastClick,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'updatedAt' | 'createdAt'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [forecastToDelete, setForecastToDelete] = useState<ForecastProject | null>(null);

  // Fetch forecasts
  const {
    forecastProjects,
    isLoadingList: isLoading,
    deleteForecastProject
  } = useForecastProject();

  // Filter and sort forecasts locally
  const filteredForecasts = useMemo(() => {
    let filtered = [...forecastProjects];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((forecast) =>
        forecast.name.toLowerCase().includes(searchLower) ||
        forecast.sourceAnalysisName?.toLowerCase().includes(searchLower)
      );
    }

    // Tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter((forecast) =>
        forecast.metadata.tags?.some(tag => selectedTags.includes(tag))
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'createdAt':
          aValue = new Date(a.metadata.createdAt).getTime();
          bValue = new Date(b.metadata.createdAt).getTime();
          break;
        case 'updatedAt':
        default:
          aValue = new Date(a.metadata.updatedAt).getTime();
          bValue = new Date(b.metadata.updatedAt).getTime();
          break;
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [forecastProjects, searchTerm, selectedTags, sortBy, sortOrder]);

  // Get all unique tags for filtering
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    forecastProjects.forEach((forecast) => {
      forecast.metadata.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [forecastProjects]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
  };

  // Handle forecast click - close modal and navigate with forecast ID
  const handleForecastClickInternal = (forecast: { id: string; name: string }) => {
    onClose(); // Close the modal
    navigate('/forecast', { state: { forecastId: forecast.id } });
    // Also call the original callback for compatibility
    onForecastClick(forecast);
  };

  // Delete handlers
  const handleDeleteForecast = (forecast: ForecastProject) => {
    setForecastToDelete(forecast);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!forecastToDelete) return;

    try {
      await deleteForecastProject.mutateAsync(forecastToDelete.id);
      setShowDeleteConfirm(false);
      setForecastToDelete(null);
    } catch (err) {
      logError('Failed to delete forecast:', err);
    }
  };



  const handleEditForecast = () => {
    // Future implementation: Open edit modal or navigate to edit page
  };

  return (
    <>
      <ModalCard
        isOpen={isOpen}
        onClose={onClose}
        title="All Forecasts"
        subtitle="Browse and manage your failure prediction forecasts"
        size="xl"
        closeOnBackdrop={false}
      >
        <div className="max-h-[80vh]">
          {/* Search and Filter Bar */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            {/* Search Input */}
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search forecasts by name or source analysis..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Filter and Sort Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* Filters Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center space-x-2 px-3 py-2 border rounded-lg transition-colors ${showFilters || selectedTags.length > 0
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <FunnelIcon className="w-4 h-4" />
                  <span>Filters</span>
                  {selectedTags.length > 0 && (
                    <span className="bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {selectedTags.length}
                    </span>
                  )}
                </button>

                {/* Sort Options */}
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as 'name' | 'updatedAt' | 'createdAt');
                    setSortOrder(order as 'asc' | 'desc');
                  }}
                  className="pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="updatedAt-desc">Recently Modified</option>
                  <option value="createdAt-desc">Recently Created</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid'
                    ? 'bg-green-100 text-green-700'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  <Squares2X2Icon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list'
                    ? 'bg-green-100 text-green-700'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  <ListBulletIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="bg-gray-50 rounded-lg p-4 mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">Filter by Tags</h4>
                  {selectedTags.length > 0 && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-green-600 hover:text-green-800 font-medium"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {allTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {allTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => handleTagToggle(tag)}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${selectedTags.includes(tag)
                          ? 'bg-green-500 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No tags found in your forecasts</p>
                )}
              </div>
            )}
          </div>

          {/* Forecast Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                <span className="ml-2 text-gray-600">Loading forecasts...</span>
              </div>
            )}

            {/* Forecast List */}
            {!isLoading && (
              <>
                {filteredForecasts.length > 0 ? (
                  <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
                    {filteredForecasts.map((forecast) => (
                      <ForecastCard
                        key={forecast.id}
                        forecast={forecast}
                        onClick={() => handleForecastClickInternal({
                          id: forecast.id,
                          name: forecast.name,
                        })}
                        onEdit={handleEditForecast}
                        onDelete={() => handleDeleteForecast(forecast)}
                        variant={viewMode === 'grid' ? 'grid' : 'list'}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-gray-500 mb-2">
                      {searchTerm || selectedTags.length > 0
                        ? 'No forecasts match your filters'
                        : 'No saved forecasts found'}
                    </div>
                    {(searchTerm || selectedTags.length > 0) && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-green-600 hover:text-green-800 underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer Info */}
          {filteredForecasts.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-200 text-sm text-gray-500 flex-shrink-0">
              Showing {filteredForecasts.length} of {forecastProjects.length} forecasts
            </div>
          )}
        </div>
      </ModalCard>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && forecastToDelete && (
        <ModalCard
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Delete Forecast"
          size="sm"
        >
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-gray-900">
                  Are you sure you want to delete &quot;{forecastToDelete.name}&quot;?
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone. All forecast data will be permanently lost.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                disabled={deleteForecastProject.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteForecastProject.isPending}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteForecastProject.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </ModalCard>
      )}
    </>
  );
};

export default AllForecastsModal;